import { useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, ExternalLink, RotateCcw } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { DataTable } from '@/components/ui/DataTable';
import type { Column } from '@/components/ui/DataTable';
import { RunStatusBadge, runStatusLabel } from '@/components/ui/RunStatusBadge';
import type { BatchRunResult, BatchRunsResponse } from '@/hooks/useRuns';

/** Eindeutige Verzichts-Warnungen des Batches (gleicher Text nur einmal). */
function waiverWarnungen(result: BatchRunsResponse): string[] {
  const texte = new Set<string>();
  for (const run of result.runs) {
    if (run.snapshot_waiver_warning) texte.add(run.snapshot_waiver_warning);
  }
  return [...texte];
}

/**
 * Ergebnis der Komplettwartung.
 *
 * Bewusst KEIN automatischer Sprung in einen Run: es sind mehrere. Jeder
 * erzeugte Lauf steht mit Link da, Teilfehler stehen getrennt darunter mit
 * Grund und lassen sich gezielt erneut anstoßen.
 */

interface BatchResultCardProps {
  customerId: string;
  customerName: string;
  result: BatchRunsResponse;
  /** routine_slug -> Anzeigename aus der Vorschau; fehlt = Slug anzeigen. */
  routineNames: Record<string, string>;
  onRetryFailed: (slugs: string[]) => void;
}

export function BatchResultCard({
  customerId,
  customerName,
  result,
  routineNames,
  onRetryFailed,
}: BatchResultCardProps): JSX.Element {
  const started = result.runs.length;
  const failed = result.errors.length;
  const reused = result.runs.filter((r) => !r.created).length;

  const columns = useMemo((): ReadonlyArray<Column<BatchRunResult>> => {
    return [
      {
        id: 'routine',
        header: 'Routine',
        filterValue: (run) =>
          `${routineNames[run.routine_slug] ?? run.routine_slug} ${run.routine_slug}`,
        cell: (run) => (
          <>
            <div className="font-medium">
              {routineNames[run.routine_slug] ?? run.routine_slug}
            </div>
            <div className="font-mono text-xs text-muted">{run.routine_slug}</div>
          </>
        ),
      },
      {
        id: 'mode',
        header: 'Mode',
        filter: 'select',
        filterValue: (run) => (run.mode === 'check' ? 'Dry-Run' : run.mode),
        cell: (run) => (
          <Badge tone={run.mode === 'check' ? 'neutral' : 'danger'}>
            {run.mode === 'check' ? 'Dry-Run' : run.mode}
          </Badge>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        filter: 'select',
        filterValue: (run) => {
          const label = runStatusLabel(run.status);
          return run.created ? label : `${label} bereits vorhanden`;
        },
        cell: (run) => (
          <span className="flex flex-wrap items-center gap-1">
            <RunStatusBadge status={run.status} />
            {!run.created && <Badge tone="neutral">bereits vorhanden</Badge>}
          </span>
        ),
      },
      {
        id: 'snapshots',
        header: 'Snapshots',
        className: 'text-xs text-muted',
        filterValue: (run) =>
          run.snapshot_ids.length > 0 ? String(run.snapshot_ids.length) : '',
        cell: (run) => (run.snapshot_ids.length > 0 ? run.snapshot_ids.length : '–'),
      },
      {
        id: 'aktion',
        header: ' ',
        filter: false,
        cell: (run) => (
          <Link
            to="/runs/$id"
            params={{ id: run.run_id }}
            className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
          >
            Lauf öffnen
            <ExternalLink className="size-3" aria-hidden="true" />
          </Link>
        ),
      },
    ];
  }, [routineNames]);

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <span>Ergebnis</span>
          {/* "teilweise" wäre gelogen, wenn KEIN Lauf zustande kam. */}
          {failed === 0 ? (
            <Badge tone="success">vollstaendig</Badge>
          ) : started === 0 ? (
            <Badge tone="danger">nichts gestartet</Badge>
          ) : (
            <Badge tone="warning">teilweise</Badge>
          )}
        </span>
      }
      description={`Komplettwartung für ${customerName}.`}
    >
      {/* Gewohnheitsbremse des Servers (3. Snapshot-Verzicht des Kunden in 90
          Tagen). Sie steht an jedem betroffenen Lauf mit demselben Text -
          einmal anzeigen genügt, aber anzeigen MUSS sie hier: sonst liest
          die Warnung wieder niemand. */}
      {waiverWarnungen(result).map((text) => (
        <p
          key={text}
          role="alert"
          data-testid="batch-waiver-warning"
          className="mb-2 flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-3 text-sm"
        >
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden="true" />
          <span>{text}</span>
        </p>
      ))}

      <p className="text-sm" role="status" data-testid="batch-result-summary">
        <strong>{started}</strong> {started === 1 ? 'Lauf' : 'Läufe'} gestartet
        {failed > 0 && (
          <>
            , <strong className="text-danger">{failed}</strong> fehlgeschlagen
          </>
        )}
        .
        {reused > 0 && (
          <span className="text-muted">
            {' '}
            {reused} davon existierte{reused === 1 ? '' : 'n'} schon (gleicher
            Idempotency-Key).
          </span>
        )}
      </p>

      {started > 0 && (
        <div className="mt-3">
          <DataTable
            caption="Erzeugte Läufe"
            columns={columns}
            rows={result.runs}
            rowKey={(run) => run.run_id}
            storageKey="batch-result-runs"
            rowTestId={(run) => `batch-result-run-${run.routine_slug}`}
          />
        </div>
      )}

      {failed > 0 && (
        <div
          className="mt-4 rounded-md border border-danger/40 bg-danger/5 p-3"
          role="alert"
          data-testid="batch-result-errors"
        >
          <p className="flex items-center gap-2 text-sm font-medium text-danger">
            <AlertTriangle className="size-4" aria-hidden="true" />
            {failed} {failed === 1 ? 'Routine' : 'Routinen'} nicht gestartet
          </p>
          <ul className="mt-2 flex flex-col gap-1 text-sm">
            {result.errors.map((err) => (
              <li
                key={err.routine_slug}
                className="flex flex-wrap items-baseline gap-2"
                data-testid={`batch-result-error-${err.routine_slug}`}
              >
                <span className="font-medium">
                  {routineNames[err.routine_slug] ?? err.routine_slug}
                </span>
                <code className="font-mono text-xs text-muted">{err.routine_slug}</code>
                <span>{err.message}</span>
              </li>
            ))}
          </ul>
          <div className="mt-3">
            <Button
              size="sm"
              variant="secondary"
              leftIcon={<RotateCcw className="size-4" />}
              onClick={() => onRetryFailed(result.errors.map((e) => e.routine_slug))}
              data-testid="batch-retry-failed"
            >
              Nur die fehlgeschlagenen erneut vorbereiten
            </Button>
          </div>
        </div>
      )}

      <footer className="mt-4 flex flex-wrap gap-2">
        <Link to="/runs">
          <Button variant="secondary">Run-Liste öffnen</Button>
        </Link>
        <Link to="/customers/$id" params={{ id: customerId }} search={{}}>
          <Button variant="ghost">Zurück zum Kunden</Button>
        </Link>
      </footer>
    </Card>
  );
}
